import React from 'react';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Typography } from '@mui/material';

import { OptimizationCostComparison } from '@paragon/data-connections';

import { formatOptimizationCost } from './formatOptimizationCost';
import { calculateOptimizationSavings } from './optimizationCostBreakdown';

interface Props {
  readonly comparison: OptimizationCostComparison;
  /** What to call the design the run started from -- the component's own, until its optimized truss is adopted. */
  readonly currentLabel: string;
  /** What to call the design being priced against the current one -- it is only the best so far until the run ends. */
  readonly optimizedLabel: string;
}

/**
 * The bottom line of an optimization's before/after while it is still running: what the current design costs against
 * what the best truss found so far would, and the saving between them.
 */
export function RunningCostComparison({ comparison, currentLabel, optimizedLabel }: Props) {
  const savings = calculateOptimizationSavings(comparison.previousDesign.total, comparison.optimizedDesign.total);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <CostRow label={currentLabel} amount={comparison.previousDesign.total} />
      <CostRow label={optimizedLabel} amount={comparison.optimizedDesign.total} savings={savings} />
      {comparison.lumberOnly && (
        <Typography variant="caption" color="text.secondary">
          Plate cost excluded
        </Typography>
      )}
    </Box>
  );
}

interface CostRowProps {
  readonly label: string;
  readonly amount: number;
  readonly savings?: number;
}

function CostRow({ label, amount, savings }: CostRowProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatOptimizationCost(amount)}
      </Typography>
      {savings != null && <Savings savings={savings} />}
    </Box>
  );
}

function Savings({ savings }: { readonly savings: number }) {
  const cents = Math.round(savings * 100);
  const color = cents > 0 ? 'success.main' : cents < 0 ? 'warning.main' : 'text.secondary';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, color, whiteSpace: 'nowrap', minWidth: '5em' }}>
      {cents > 0 && <ArrowDownwardIcon sx={{ fontSize: '0.9rem' }} />}
      {cents < 0 && <ArrowUpwardIcon sx={{ fontSize: '0.9rem' }} />}
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatOptimizationCost(Math.abs(cents) / 100)}
      </Typography>
    </Box>
  );
}
